import { useEffect } from 'react';
import { NavLink, useLocation } from 'react-router';
import { matchesPath } from '../modules/navigation';
import { useNavigationTree } from '../modules/useNavigationTree';
import { SidebarGroup, SidebarItem, subNavLinkClass } from './Sidebar';

interface ModuleNavigationProps {
  isCollapsed: boolean;
  openMenu: string | null;
  setOpenMenu: (key: string | null) => void;
  /** Called after a link was followed (closes the mobile sidebar). */
  onNavigate: () => void;
}

/**
 * Sidebar navigation built from the menu entries in the database (label,
 * icon, position, visibility) and the nav nodes of the active modules.
 */
export function ModuleNavigation({ isCollapsed, openMenu, setOpenMenu, onNavigate }: ModuleNavigationProps) {
  const location = useLocation();
  const tree = useNavigationTree();

  // Expand the group of the current page (not in collapsed mode, where groups are flyouts).
  useEffect(() => {
    if (isCollapsed) return;
    const group = tree.find(n => n.kind === 'group' && n.items.some(i => matchesPath(location.pathname, i.path)));
    if (group && openMenu !== group.key) setOpenMenu(group.key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, tree]);

  const followLink = () => {
    onNavigate();
    if (isCollapsed) setOpenMenu(null);
  };

  return (
    <>
      {tree.map(node => {
        if (node.kind === 'link') {
          return (
            <SidebarItem
              key={node.key}
              to={node.path}
              icon={node.icon}
              label={node.label}
              isCollapsed={isCollapsed}
              end={node.end}
              onClick={onNavigate}
            />
          );
        }
        const Header = node.header;
        return (
          <SidebarGroup
            key={node.key}
            id={node.key}
            icon={node.icon}
            label={node.label}
            isOpen={openMenu === node.key}
            isCollapsed={isCollapsed}
            onToggle={key => setOpenMenu(openMenu === key ? null : key)}
          >
            {Header && (
              <div className="mb-2">
                <Header onSelect={() => { if (isCollapsed) setOpenMenu(null); }} />
              </div>
            )}
            <div className="space-y-0.5 mt-1">
              {node.items.map(item => (
                <div key={item.key}>
                  {item.section && (
                    <div className="pt-3 pb-1">
                      <label className="text-[10px] uppercase text-primary-400 font-bold px-1 mb-1 block tracking-wider">
                        {item.section}
                      </label>
                    </div>
                  )}
                  <NavLink to={item.path} className={subNavLinkClass} onClick={followLink}>
                    {!isCollapsed && <item.icon size={16} />}
                    <span>{item.label}</span>
                  </NavLink>
                </div>
              ))}
            </div>
          </SidebarGroup>
        );
      })}
    </>
  );
}
